import React, { useState, useEffect } from 'react';
import { FiX, FiAlertCircle } from 'react-icons/fi';

const AccessAssignmentModal = ({ assignment, allowRoleChoice, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    role: 'counselor',
    campuses: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    if (assignment) {
      setFormData({
        email: assignment.email || '',
        role: assignment.role || 'counselor',
        campuses: (assignment.campuses || []).join('\n')
      });
    }
  }, [assignment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setWarning('');

    if (!formData.email) {
      setError('Email is required');
      return;
    }

    const campuses = formData.campuses
      .split(/[\n,]/)
      .map((c) => c.trim())
      .filter(Boolean);

    if (formData.role === 'counselor' && campuses.length === 0) {
      setError('At least one campus is required for a counselor');
      return;
    }

    setLoading(true);
    try {
      const result = await onSave({ email: formData.email, role: formData.role, campuses });
      if (result?.warning) {
        setWarning(result.warning);
        setLoading(false);
        return;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save assignment');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {assignment ? 'Edit Assignment' : 'Add Assignment'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {warning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start">
              <FiAlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p>{warning}</p>
                <p className="mt-1">Saved anyway — double-check the campus name matches the roster.</p>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={!!assignment}
              required
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore
              data-bwignore="true"
              data-form-type="other"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="counselor@district.org"
            />
          </div>

          {allowRoleChoice && (
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="counselor">Counselor</option>
                <option value="districtAdmin">District Admin</option>
              </select>
              {assignment && formData.role !== assignment.role && (
                <p className="mt-1 text-xs text-amber-600">
                  {formData.role === 'districtAdmin'
                    ? 'This will grant full-district access and remove any campus restriction.'
                    : 'This will remove full-district access — campuses will need to be set.'}
                </p>
              )}
            </div>
          )}

          {formData.role === 'counselor' && (
            <div>
              <label htmlFor="campuses" className="block text-sm font-medium text-gray-700 mb-1">
                Campuses <span className="text-red-600">*</span>
              </label>
              <textarea
                id="campuses"
                name="campuses"
                value={formData.campuses}
                onChange={handleChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                placeholder={'One campus per line, e.g.\nGARLAND H S (002)\nROWLETT H S (009)'}
              />
              <p className="mt-1 text-xs text-gray-500">
                Must match the campus name exactly as it appears in the roster (Campus/Entity).
              </p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : assignment ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccessAssignmentModal;
